import { useEffect, useRef } from 'react';
import { CaretLeftIcon } from '@phosphor-icons/react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  useSidebar,
} from '@/components/ui-kit/sidebar';
import { getSidebarStyle } from '@/lib/utils/sidebar-utils';
import { useFilteredMenu } from '@/hooks/use-filtered-menu';
import { LogoSection, MenuSection } from '@/components/core';
import { menuItems } from '@/constant/sidebar-menu';
import {
  isSettingsSectionPath,
  settingsOverviewItem,
  settingsWorkspaceItems,
  settingsSendingItems,
  settingsComplianceItems,
  settingsDeveloperItems,
} from '@/constant/settings-sidebar-menu';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui-kit/button';

const AUDIENCE_IDS = new Set(['contacts', 'lists', 'segments', 'suppression']);
/** Prototype labels this "Campaigns"; keep id set name aligned with product area. */
const CAMPAIGNS_IDS = new Set(['campaigns', 'templates', 'media-library']);
const REPORTS_IDS = new Set(['reports-overview', 'reports-deliverability']);
const SETTINGS_IDS = new Set(['settings']);

/**
 * AppSidebar — Campaign Tool navigation with a 64px icon rail when collapsed.
 * Collapse via the header caret; expand by clicking the logo mark (no hover flyout).
 */
export const AppSidebar = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { setOpenMobile, open, setOpen, isMobile, openMobile, state } = useSidebar();

  const filteredMenuItems = useFilteredMenu(menuItems);
  const audienceItems = filteredMenuItems.filter((item) => AUDIENCE_IDS.has(item.id));
  const campaignsItems = filteredMenuItems.filter((item) => CAMPAIGNS_IDS.has(item.id));
  const reportsItems = filteredMenuItems.filter((item) => REPORTS_IDS.has(item.id));
  const settingsItems = filteredMenuItems.filter((item) => SETTINGS_IDS.has(item.id));
  const isSettingsSection = isSettingsSectionPath(pathname);

  // Settings has its own multi-page nav (Overview / Users & roles / Custom
  // fields), each a normal pushed route. navigate(-1) would only step back
  // through those instead of leaving the section, so the last URL visited
  // before entering Settings is tracked here and used as the back target.
  const lastNonSettingsLocationRef = useRef('/contacts');
  useEffect(() => {
    if (!isSettingsSection) {
      lastNonSettingsLocationRef.current = `${pathname}${search}`;
    }
  }, [pathname, search, isSettingsSection]);

  const handleBackFromSettings = () => navigate(lastNonSettingsLocationRef.current);

  useEffect(() => {
    if (!isMobile) {
      setOpenMobile(false);
    }
  }, [pathname, setOpenMobile, isMobile]);

  const sidebarStyle = getSidebarStyle(isMobile, open, openMobile);
  const showText = open || isMobile;
  const onItemClick = isMobile ? () => setOpenMobile(false) : undefined;
  const isRail = !isMobile && state === 'collapsed';

  if (isMobile && !openMobile) {
    return null;
  }

  return (
    <Sidebar
      className={cn(
        'h-full border-r border-sidebar-border bg-sidebar',
        isMobile && 'mobile-sidebar'
      )}
      collapsible={isMobile ? 'none' : 'icon'}
      style={sidebarStyle}
    >
      {isMobile ? (
        <SidebarHeader className="flex h-14 shrink-0 flex-row items-center gap-0 border-b border-border p-0 px-3">
          {isSettingsSection ? (
            <Button
              type="button"
              variant="ghost"
              onClick={handleBackFromSettings}
              className="h-auto gap-1.5 rounded-md px-2.5 py-2 text-base font-bold text-high-emphasis hover:bg-neutral-100"
              aria-label="Back"
            >
              <CaretLeftIcon className="size-4 shrink-0" />
              <span>Settings</span>
            </Button>
          ) : (
            <LogoSection open={open} isMobile={isMobile} onClose={() => setOpenMobile(false)} />
          )}
        </SidebarHeader>
      ) : (
        <SidebarHeader className="flex shrink-0 flex-row items-center gap-1 border-0 p-0 px-2 pb-4 pt-5">
          {isSettingsSection ? (
            <Button
              type="button"
              variant="ghost"
              onClick={handleBackFromSettings}
              className={cn(
                'h-auto gap-1.5 rounded-md px-2.5 py-2 text-base font-bold text-high-emphasis hover:bg-neutral-100',
                !showText && 'justify-center px-0'
              )}
              aria-label="Back"
            >
              <CaretLeftIcon className="size-4 shrink-0" />
              {showText && <span>Settings</span>}
            </Button>
          ) : (
            <LogoSection
              open={open}
              isMobile={false}
              onClose={() => setOpenMobile(false)}
              onExpand={isRail ? () => setOpen(true) : undefined}
            />
          )}
          {!isRail && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="nav-toggle ml-auto size-7 shrink-0 rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
              aria-controls="app-sidebar"
              aria-expanded
              aria-label="Collapse navigation"
              title="Collapse navigation"
              onClick={() => setOpen(false)}
            >
              <CaretLeftIcon className="size-[15px]" />
            </Button>
          )}
        </SidebarHeader>
      )}

      {isSettingsSection ? (
        <SidebarContent
          id="app-sidebar"
          className={cn(
            'flex flex-col gap-0 overflow-x-hidden py-3 text-sm',
            isRail ? 'px-2' : 'px-3'
          )}
        >
          <MenuSection
            items={[settingsOverviewItem]}
            showText={showText}
            pathname={pathname}
            onItemClick={onItemClick}
          />
          <MenuSection
            title="Workspace"
            className="mt-4"
            items={settingsWorkspaceItems}
            showText={showText}
            pathname={pathname}
            onItemClick={onItemClick}
          />
          <MenuSection
            title="Sending"
            className="mt-4"
            items={settingsSendingItems}
            showText={showText}
            pathname={pathname}
            onItemClick={onItemClick}
          />
          <MenuSection
            title="Compliance"
            className="mt-4"
            items={settingsComplianceItems}
            showText={showText}
            pathname={pathname}
            onItemClick={onItemClick}
          />
          <>
            <MenuSection
              title="Developers"
              className="mt-4"
              items={settingsDeveloperItems}
              showText={showText}
              pathname={pathname}
              onItemClick={onItemClick}
            />
          </>
        </SidebarContent>
      ) : (
        <>
          <SidebarContent
            id="app-sidebar"
            className={cn(
              'flex flex-col gap-0 overflow-x-hidden py-3 text-sm',
              isRail ? 'px-2' : 'px-3'
            )}
          >
            <MenuSection
              title="Audience"
              items={audienceItems}
              showText={showText}
              pathname={pathname}
              onItemClick={onItemClick}
            />
            <MenuSection
              title="Campaigns"
              className="mt-4"
              items={campaignsItems}
              showText={showText}
              pathname={pathname}
              onItemClick={onItemClick}
            />
            <MenuSection
              title="Reports"
              className="mt-4"
              items={reportsItems}
              showText={showText}
              pathname={pathname}
              onItemClick={onItemClick}
            />
          </SidebarContent>

          <SidebarFooter
            className={cn(
              'nav-pinned mt-auto gap-0 border-0 border-t border-sidebar-border p-0 pt-4',
              isRail ? 'px-2 pb-3' : 'px-3 pb-5'
            )}
          >
            <MenuSection
              items={settingsItems}
              showText={showText}
              pathname={pathname}
              onItemClick={onItemClick}
              pinned
            />
          </SidebarFooter>
        </>
      )}
    </Sidebar>
  );
};
